import React, { useCallback, useState } from 'react';
import { SmallTransparentButton } from 'shared/components/Buttons';
import { FormSearchInput } from 'shared/components/FormSearchInput';
import { ListWithItemDrag } from 'shared/components/ListWithRowDrag';
import { MetricParameterDraggableItem } from './MetricParameterDraggableItem';
import './ParametersSelection.scss';
import { ParametersDefaultOptions, SearchAbleMockOptions } from '../mocks';
import { MetricsDraggableProps } from '../types';

export interface ParametersSelectionProps {
  data: MetricsDraggableProps[];
  onDataChange: (data: MetricsDraggableProps[]) => void;
  disabled?: boolean;
}

export const ParametersSelection: React.FC<ParametersSelectionProps> = ({
  data,
  onDataChange,
  disabled = false,
}) => {
  const [input, setInput] = useState('');

  const handleUpdateOption = useCallback(
    (value: string, index: number) => {
      const updated = [...data];
      updated[index].type = value;
      onDataChange(updated);
    },
    [data, onDataChange]
  );
  const handleDataChange = useCallback(
    (newData: MetricsDraggableProps[]) => {
      onDataChange(newData);
    },
    [onDataChange]
  );

  const handleDeletion = useCallback(
    (index: number) => {
      const updated = [...data].filter((_, i) => i !== index);
      onDataChange(updated);
    },
    [data, onDataChange]
  );

  const handleAddNew = useCallback(() => {
    if (input && input !== '') {
      const updated = [
        ...data,
        {
          text: input,
          type: ParametersDefaultOptions[0],
        },
      ];

      onDataChange(updated);
      setInput('');
    }
  }, [data, input, onDataChange]);

  return (
    <div className="parameters-selection">
      <div className="parameters-selection__input-area">
        <FormSearchInput
          label="Parameters"
          placeholder="Search"
          searchInput={input}
          setSearchInput={setInput}
          optionsList={SearchAbleMockOptions}
          disabled={disabled}
        />
        {!disabled ? (
          <SmallTransparentButton
            onClick={handleAddNew}
            text="+ Add Parameter"
            disabled={disabled}
          />
        ) : null}
      </div>

      <div className="parameters-selection__draggable-box">
        <ListWithItemDrag
          keyExpr="text"
          data={data}
          onDataChange={handleDataChange}
          disabled={disabled}
          itemTemplate={(
            { text, type, selectedOption }: MetricsDraggableProps,
            index
          ) => (
            <MetricParameterDraggableItem
              text={text}
              type={type}
              selectedOption={selectedOption}
              options={ParametersDefaultOptions}
              onRemove={() => handleDeletion(index)}
              onChangeOption={(value) => handleUpdateOption(value, index)}
              disabled={disabled}
            />
          )}
        />
      </div>
    </div>
  );
};
